import { get, includes, replace } from 'lodash';

import PropTypes from 'prop-types';
import React, {
    useCallback,
    useEffect,
    useState,
    useRef,
    useContext,
} from 'react';
import { DialogContent, DialogTitle } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Input from '../../material-ui-kit/CustomInput/CustomInput';
import Button from '../../material-ui-kit/CustomButtons/Button';
import Radio from '../../material-ui-kit/Radio/Radio';
import { EMPLOYER_REP_ROLES } from '../../../constants/EmployerRep';
import useStyles from './EmployerRepEditModalStyle';
import NotificationContext from '../../../contexts/NotificationContext';
import EmployerContext from '../../../contexts/EmployerContext';
import { checkIfEmailExists } from './EmployerRepsListUtils';
import CustomSelect from '../../material-ui-kit/CustomSelect/Select';
import { EmployerRepAuthTypes } from '../../../constants/employerRepaAuthTypes';
import {
    LinkHasProtocolRegex,
    LinkedinProfileRegex,
} from '../../../constants/Regex';

const { SSO, PAP } = EmployerRepAuthTypes;

export const roleOptions = [
    { value: EMPLOYER_REP_ROLES.HR, label: 'HR_DESCRIPTION' },
    { value: EMPLOYER_REP_ROLES.NON_HR, label: 'NON_HR_DESCRIPTION' },
    { value: EMPLOYER_REP_ROLES.HIRING_MANAGER, label: 'HIRING_MANAGER_DESCRIPTION' },
    { value: EMPLOYER_REP_ROLES.SALES, label: 'SALES_DESCRIPTION' },
];

export const connectionOptions = [
    { value: SSO, label: 'AUTH_TYPE_SSO_LABEL' },
    { value: PAP, label: 'AUTH_TYPE_POP_LABEL' },
];

const checkIfFormValid = (employerData) => {
    const trimmedName = employerData.name.trim();
    const trimmedEmail = employerData.email.trim();
    const trimmedEmail2 = employerData.email2.trim();

    const isNameValid = trimmedName.length > 0;
    const isEmailValid = trimmedEmail.length && checkIfEmailValid(trimmedEmail);
    const isEmail2Valid = !trimmedEmail2.length || checkIfEmailValid(trimmedEmail2);

    return isNameValid && isEmailValid && isEmail2Valid;
};

// eslint-disable-next-line no-useless-escape
const checkIfEmailValid = (email) => email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9\-\.]+)\.([a-zA-Z]{2,5})$/);

// Modified by AI on 05/26/2026. Edit #1.
const EmployerRepEditModal = ({
    employerRep = {
        name: '',
        email: '',
        roles: [EMPLOYER_REP_ROLES.HR, EMPLOYER_REP_ROLES.USER],
        phones: [],
        title: '',
    },
    existingEmails = [],
    onConfirm = () => {},
    onCancel = () => {},
    title = 'EMPLOYER_REP_DETAILS',
    isEditMode = false,
}) => {
    const isFirstRender = useRef(true);
    const { t } = useTranslation();
    const { showErrorNotification } = useContext(NotificationContext);
    const { isDepartmentsEnabled, employer } = useContext(EmployerContext);
    const employerRepAuthType = get(employer, 'settings.authData.authType', PAP);
    const employerRepExtraAuthTypes = get(employer, 'settings.authData.extraAuthTypes', []);
    const hasPAPExtraAuthType = employerRepExtraAuthTypes.includes(PAP);
    const isPAPAuthTypeAvailable = employerRepAuthType === PAP || hasPAPExtraAuthType;
    const isConnectionShown = employerRepAuthType === SSO && hasPAPExtraAuthType;
    const classes = useStyles({ isConnectionShown });

    const defaultAuthType = employerRepAuthType || PAP;

    const [employerData, setEmployerData] = useState({
        linkedin: '',
        phone: '',
        email: '',
        email2: '',
        title: '',
        name: '',
        isAdmin: false,
        role: EMPLOYER_REP_ROLES.HR,
        company: '',
        authType: defaultAuthType,
    });
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFieldWithError, setIsFieldWithError] = useState(false);
    const [isLinkedinLinkInvalid, setIsLinkedinLinkInvalid] = useState(false);
    const [isLinkedinEditing, setIsLinkedinEditing] = useState(false);
    const [linkedinUrlWidth, setLinkedinUrlWidth] = useState(0);

    useEffect(() => {
        if (employerRep) {
            setEmployerData({
                linkedin: get(employerRep, 'linkedin', ''),
                phone: get(employerRep, 'phones.0.value', ''),
                email: get(employerRep, 'email', ''),
                email2: get(employerRep, 'email2', ''),
                title: get(employerRep, 'title', ''),
                name: get(employerRep, 'name', ''),
                isAdmin: employerRep.roles.includes(EMPLOYER_REP_ROLES.ADMIN),
                role: employerRep.roles.find((role) => !includes([EMPLOYER_REP_ROLES.ADMIN, EMPLOYER_REP_ROLES.USER], role)),
                company: get(employerRep, 'employerDepartment.name', ''),
                authType: get(employerRep, 'authType', defaultAuthType),
            });
        }
    }, [employerRep]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        setIsFormValid(checkIfFormValid(employerData));
    }, [employerData]);

    useEffect(() => {
        const chars = get(employerData, 'linkedin.length', 0);
        if (chars > 0) {
            setLinkedinUrlWidth(chars * 6.5);
            return;
        }

        setLinkedinUrlWidth(0);
    }, [employerData?.linkedin]);

    const openLinkedinProfile = useCallback(() => {
        const linkedinProfile = employerData?.linkedin;
        if (!linkedinProfile) {
            return;
        }
        const url = !linkedinProfile.match(LinkHasProtocolRegex) ? `https://${linkedinProfile}` : linkedinProfile;
        window.open(url, '_blank');
    }, [employerData?.linkedin]);

    const handleConfirm = useCallback(() => {
        if (!checkIfFormValid(employerData)) {
            showErrorNotification('FORM_IS_NOT_VALID');
            return;
        }

        if (!isEditMode && checkIfEmailExists(existingEmails, employerData.email)) {
            showErrorNotification('ADD_USER_EMAIL_ALREADY_EXIST');
            return;
        }

        if (employerData.email2 === employerData.email) {
            showErrorNotification('SECOND_USER_EMAIL_EQUAL_TO_FIRST_EMAIL');
            setIsFieldWithError(true);
            return;
        }

        const isEmail2Changed = get(employerRep, 'email2', '') !== employerData.email2;

        if (isEmail2Changed && checkIfEmailExists(existingEmails, employerData.email2)) {
            showErrorNotification('SECOND_USER_EMAIL_EQUAL_TO_SOMEONE_ELSES_EMAIL');
            setIsFieldWithError(true);
            return;
        }

        if (employerData?.linkedin) {
            const { linkedin } = employerData;
            if (!linkedin.match(LinkedinProfileRegex)) {
                showErrorNotification('INVALID_LINKEDIN_PROFILE_URL');
                setIsLinkedinLinkInvalid(true);
                return;
            }
        }

        onConfirm({
            ...employerRep,
            linkedin: (employerData.linkedin || '').trim(),
            email: (employerData.email || '').trim(),
            email2: (employerData.email2 || '').trim(),
            name: (employerData.name || '').trim(),
            title: (employerData.title || '').trim(),
            company: (employerData.company || '').trim(),
            phones: employerData.phone ? [{
                value: employerData.phone,
                type: 'Work',
            }] : [],
            roles: [
                ...(employerData.isAdmin ? [EMPLOYER_REP_ROLES.ADMIN] : [EMPLOYER_REP_ROLES.USER]),
                ...(employerData.role ? [employerData.role] : [EMPLOYER_REP_ROLES.HR]),
            ],
            authType: employerData.authType || PAP,
        });
    }, [employerData, employerRep, existingEmails]);

    return (
        <div data-test-id='add-user-dialog-form'>
            <DialogTitle>
                <div className={classes.title}>
                    <span className={classes.titleLabel} dangerouslySetInnerHTML={{ __html: t(title) }}/>
                </div>
            </DialogTitle>
            <DialogContent>
                <div className={classes.content}>
                    {
                        <div className={classes.connectionTypes}>
                            <p>{t('CONNECTION', 'Connection')}</p>
                            <FormControl className={classes.radioField} component='fieldset'>
                                <RadioGroup aria-label='noteType'
                                    name='authType'
                                    value={employerData.authType}
                                    defaultValue={defaultAuthType}
                                    onChange={(e) => {
                                        if (isEditMode) return;
                                        setEmployerData({
                                            ...employerData,
                                            authType: e.target.value,
                                        });
                                    }}>
                                    {
                                        employerRepAuthType === SSO && <FormControlLabel
                                            disabled={isEditMode}
                                            value={SSO}
                                            control={<Radio testId='auth-type-sso'/>}
                                            label={t('AUTH_TYPE_SSO_LABEL', 'Single Sign On')}
                                        />
                                    }
                                    {
                                        isPAPAuthTypeAvailable && <FormControlLabel
                                            disabled={isEditMode}
                                            value={PAP}
                                            control={<Radio testId='auth-type-pap'/>}
                                            label={t('AUTH_TYPE_PAP_LABEL', 'Standard Username & Password')}
                                        />
                                    }
                                </RadioGroup>
                            </FormControl>
                        </div>
                    }
                    <Input
                        testId='aud-user-name-input'
                        classes={{ input: classes.input }}
                        labelText={t('EMPLOYER_REP_MANAGE_FORM_NAME_LABEL')}
                        inputProps={{
                            type: 'text',
                            placeholder: t('EMPLOYER_REP_MANAGE_FORM_NAME_PLACEHOLDER'),
                            value: employerData.name,
                            onChange: (e) => {
                                setEmployerData({
                                    ...employerData,
                                    name: replace(e.target.value, /[<>]/g, ''), // prevent write html tags
                                });
                            },
                        }}
                        formControlProps={{
                            required: true,
                            className: classes.inputContainer,
                        }}
                    />
                    <Input
                        testId='aud-user-email-input'
                        classes={{ input: classes.input }}
                        labelText={t('EMPLOYER_REP_MANAGE_FORM_EMAIL_LABEL')}
                        inputProps={{
                            type: 'text',
                            placeholder: t('EMPLOYER_REP_MANAGE_FORM_EMAIL_PLACEHOLDER'),
                            value: employerData.email,
                            disabled: isEditMode,
                            onChange: (e) => {
                                setEmployerData({
                                    ...employerData,
                                    email: e.target.value.trim().toLowerCase(),
                                });
                            },
                        }}
                        formControlProps={{
                            required: true,
                            className: classes.inputContainer,
                        }}
                    />
                    {isEditMode && <Input
                        testId='aud-user-email2-input'
                        classes={{ input: classes.input }}
                        labelText={t('EMPLOYER_REP_MANAGE_FORM_EMAIL2_LABEL')}
                        inputProps={{
                            type: 'text',
                            placeholder: t('EMPLOYER_REP_MANAGE_FORM_EMAIL2_PLACEHOLDER'),
                            value: employerData.email2,
                            onChange: (e) => {
                                setIsFieldWithError(false);
                                setEmployerData({
                                    ...employerData,
                                    email2: e.target.value.trim().toLowerCase(),
                                });
                            },
                        }}
                        formControlProps={{
                            required: false,
                            className: classes.inputContainer,
                        }}
                        error={isFieldWithError}
                    />}
                    {
                        !isEditMode && <CustomSelect
                            classes={{
                                select: classes.filterSelect,
                                root: `${classes.filterRoot} ${classes.dateFilterRoot}`,
                                selectMenuItem: classes.selectMenuItem,
                            }}
                            testId={'actions-dd-swith-btn'}
                            value={employerData.role}
                            onChange={(e) => {
                                setEmployerData({
                                    ...employerData,
                                    role: e.target.value,
                                });
                            }}
                            options={roleOptions}
                            formControlProps={{
                                fullWidth: true,
                                className: classes.statusFilter,
                            }}
                        />
                    }
                    <div className={classes.linkedinInputContainer}>
                        <Input
                            testId='aud-user-linkedin-input'
                            classes={{ input: classes.input }}
                            labelText={t('EMPLOYER_REP_MANAGE_FORM_LINKEDIN_LABEL')}
                            inputProps={{
                                type: 'text',
                                placeholder: t('EMPLOYER_REP_MANAGE_FORM_LINKEDIN_PLACEHOLDER'),
                                value: employerData.linkedin,
                                onChange: (e) => {
                                    setIsLinkedinLinkInvalid(false);
                                    setEmployerData({
                                        ...employerData,
                                        linkedin: replace(e.target.value, /[<>]/g, ''),
                                    });
                                },
                                onFocus: () => { setIsLinkedinEditing(true); },
                                onBlur: () => { setIsLinkedinEditing(false); },
                            }}
                            formControlProps={{
                                required: false,
                                className: classes.linkedinInput,
                            }}
                            error={isLinkedinLinkInvalid}
                        />
                        { !isLinkedinEditing && employerData.linkedin && <a className={classes.linkedinUrl} onClick={openLinkedinProfile} target={'_blank'} rel="noreferrer" style={{ width: `${linkedinUrlWidth}px` }}/> }
                    </div>

                    <Input
                        testId='aud-user-phone-input'
                        classes={{ input: classes.input }}
                        labelText={t('EMPLOYER_REP_MANAGE_FORM_PHONE_LABEL')}
                        inputProps={{
                            type: 'tel',
                            placeholder: t('EMPLOYER_REP_MANAGE_FORM_PHONE_PLACEHOLDER'),
                            value: employerData.phone,
                            onChange: (e) => {
                                setEmployerData({
                                    ...employerData,
                                    phone: e.target.value,
                                });
                            },
                        }}
                        formControlProps={{
                            required: false,
                            className: classes.inputContainer,
                        }}
                    />
                    <Input
                        testId='aud-user-title-input'
                        classes={{ input: classes.input }}
                        labelText={t('EMPLOYER_REP_MANAGE_FORM_TITLE_LABEL')}
                        inputProps={{
                            type: 'text',
                            placeholder: t('EMPLOYER_REP_MANAGE_FORM_TITLE_PLACEHOLDER'),
                            value: employerData.title,
                            onChange: (e) => {
                                setEmployerData({
                                    ...employerData,
                                    title: e.target.value,
                                });
                            },
                        }}
                        formControlProps={{
                            required: false,
                            className: classes.inputContainer,
                        }}
                    />
                    {
                        isEditMode && isDepartmentsEnabled && <Input
                            testId='aud-user-company-input'
                            classes={{ input: classes.input }}
                            labelText={t('EMPLOYER_REP_MANAGE_FORM_COMPANY_LABEL')}
                            inputProps={{
                                type: 'text',
                                placeholder: t('EMPLOYER_REP_MANAGE_FORM_COMPANY_PLACEHOLDER'),
                                value: employerData.company,
                                disabled: true,
                                onChange: (e) => {
                                    setEmployerData({
                                        ...employerData,
                                        company: e.target.value,
                                    });
                                },
                            }}
                            formControlProps={{
                                required: false,
                                className: classes.inputContainer,
                            }}
                        />
                    }
                    {
                        !isEditMode && <FormControl className={classes.radioField} component='fieldset'>
                            <RadioGroup aria-label='noteType'
                                name='HRRole'
                                value={employerData.isAdmin ? EMPLOYER_REP_ROLES.ADMIN : EMPLOYER_REP_ROLES.USER }
                                onChange={(e) => {
                                    setEmployerData({
                                        ...employerData,
                                        isAdmin: e.target.value === EMPLOYER_REP_ROLES.ADMIN,
                                    });
                                }}>
                                <FormControlLabel
                                    value={EMPLOYER_REP_ROLES.USER}
                                    control={<Radio testId='aud-admin-checkbox'/>}
                                    label={t('USER_ACCESS_EVERYTHING_CANNOT_CREATE_USER')}/>
                                <FormControlLabel
                                    value={EMPLOYER_REP_ROLES.ADMIN}
                                    control={<Radio testId='aud-user-checkbox'/>}
                                    label={t('ADMIN_ACCESS_EVERYTHING_AND_CREATE_USERS')}/>
                            </RadioGroup>
                        </FormControl>
                    }
                    <div className={classes.footer}>
                        <div className={classes.buttons}>
                            <Button
                                className={classes.button}
                                testId='aud-cancel-btn'
                                color='tertiary'
                                onClick={onCancel}
                            >
                                {t('CANCEL')}
                            </Button>
                            <Button
                                disabled={!isFormValid}
                                className={classes.button}
                                testId='aud-invite-btn'
                                color='primary'
                                onClick={handleConfirm}>
                                {t('APPLY')}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </div>
    );
};

EmployerRepEditModal.propTypes = {
    testId: PropTypes.string,
    employerRep: PropTypes.object,
    existingEmails: PropTypes.array,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    title: PropTypes.string,
    isEditMode: PropTypes.bool,
};

export default EmployerRepEditModal;
